import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { useLogEvent } from '~/hooks/useLogEvent'

import {
  isPendingTabulationAtom,
  isTabulatableAtom,
  isTabulationFetchingAtom,
  isTabulationReadyAtom,
  whoAndWhatAwaitIdAtom,
} from '../atoms'
import { useTriggerTabulation } from '../hooks/useTriggerTabulation'
import { useWhoAndWhatTabulationQuery } from '../hooks/useWhoAndWhatTabulationQuery'

export type AnalyzeButtonProps = {
  className?: string
}

export const AnalyzeButton = ({ className }: AnalyzeButtonProps) => {
  const { t } = useTranslation()
  const isTabulatable = useAtomValue(isTabulatableAtom)
  const isTabulationFetching = useAtomValue(isTabulationFetchingAtom)
  const isTabulationReady = useAtomValue(isTabulationReadyAtom)
  const isPendingTabulation = useAtomValue(isPendingTabulationAtom)

  useWhoAndWhatTabulationQuery(useAtomValue(whoAndWhatAwaitIdAtom))

  const triggerTabulation = useTriggerTabulation()
  const hasData = isTabulationReady

  const logEvent = useLogEvent()
  const handleClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(async () => {
    const succeeded = await triggerTabulation()
    if (succeeded) {
      logEvent('Analyze who and what data')
    }
  }, [logEvent, triggerTabulation])

  if (!isTabulatable) {
    return (
      <StyledButton className={className} disabled={true}>
        {t('who_and_what_analyze')}
      </StyledButton>
    )
  }

  return (
    <StyledButton
      buttonStyle={hasData ? 'alert' : 'success'}
      className={className}
      disabled={
        isTabulationFetching || (isTabulationReady && !isPendingTabulation)
      }
      onClick={handleClick}
    >
      {hasData ? t('who_and_what_refresh') : t('who_and_what_analyze')}
    </StyledButton>
  )
}
